import { getPrintProvider } from "../print/providers";
import { brandChartPalette } from "../scene/chart";
import { opaqueHex } from "../scene/color";
import { closestWeight } from "../scene/fonts";
import type {
  BrandBinding,
  BrandBindingSlot,
  CaptionStyle,
  SceneDocument,
  SceneElement,
  ScenePage,
  Swatch,
  TextStyle,
} from "../scene/schema";
import {
  BRAND_REFERENCE_WIDTH,
  type BrandTextStyle,
  type BrandTokens,
  brandColor,
  brandRadius,
  brandSwatches,
  brandTextStyle,
} from "./tokens";

/**
 * Resolving brand bindings. Elements keep their last resolved values, so a
 * document always renders on its own; `applyBrand` refreshes those values from
 * the current kit. All functions are pure and keep unchanged objects (structural
 * sharing for the editor's undo history).
 */

/** Typography fields covered by a text style binding. */
export const TEXT_STYLE_FIELDS = [
  "fontFamily",
  "fontWeight",
  "italic",
  "fontSize",
  "lineHeight",
  "letterSpacing",
  "uppercase",
] as const satisfies readonly (keyof TextStyle)[];

export type BrandTypography = Pick<TextStyle, (typeof TEXT_STYLE_FIELDS)[number]>;

/**
 * Brand sizes refer to a 1080 px wide format. Videos use their shorter side
 * (1080 px in every video format), so a landscape video's texts are not
 * bigger than a reel's.
 */
export function brandScale(
  doc: Pick<SceneDocument, "format"> & { kind?: SceneDocument["kind"] },
): number {
  const reference =
    doc.kind === "video" ? Math.min(doc.format.width, doc.format.height) : doc.format.width;
  return reference / BRAND_REFERENCE_WIDTH;
}

const round = (value: number, digits = 1) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export function brandTypography(style: BrandTextStyle, scale: number): BrandTypography {
  return {
    fontFamily: style.fontFamily,
    fontWeight: closestWeight(style.fontFamily, style.fontWeight),
    italic: style.italic,
    fontSize: Math.max(1, round(style.fontSize * scale)),
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
    uppercase: style.uppercase,
  };
}

/**
 * Caption typography from a text style: the font, not the size (captions have
 * their own, much larger size). Captions stay bold unless the style is bolder.
 */
export const CAPTION_TEXT_FIELDS = [
  "fontFamily",
  "fontWeight",
  "italic",
  "letterSpacing",
  "uppercase",
] as const satisfies readonly (keyof CaptionStyle)[];

export function captionTypography(
  style: BrandTextStyle,
  currentWeight = 800,
): Pick<CaptionStyle, (typeof CAPTION_TEXT_FIELDS)[number]> {
  return {
    fontFamily: style.fontFamily,
    fontWeight: closestWeight(
      style.fontFamily,
      Math.max(style.fontWeight, Math.min(currentWeight, 900)),
    ),
    italic: style.italic,
    letterSpacing: style.letterSpacing,
    uppercase: style.uppercase,
  };
}

/** The brand colour with the alpha channel of the current value (e.g. 50 % transparent fills). */
function keepAlpha(brand: string, current: string): string {
  return current.length === 9 ? `${brand}${current.slice(7).toLowerCase()}` : brand;
}

function colorValue(tokens: BrandTokens, key: string, current: string): string {
  const color = brandColor(tokens, key)?.color;
  return color ? keepAlpha(color, current) : current;
}

/** Charts with `brandPalette` take their series colours from the kit. */
function applyChartPalette(element: SceneElement, tokens: BrandTokens): SceneElement {
  if (element.type !== "chart" || !element.brandPalette) return element;
  const palette = brandChartPalette(tokens);
  if (!palette || palette.join() === element.palette?.join()) return element;
  return { ...element, palette };
}

/** Colours of text marks that follow a brand colour. */
function applyMarks(element: SceneElement, tokens: BrandTokens): SceneElement {
  if (element.type !== "text" || !element.marks?.some((mark) => mark.colorToken)) return element;
  let changed = false;
  const marks = element.marks.map((mark) => {
    if (!mark.colorToken || !mark.color) return mark;
    const value = colorValue(tokens, mark.colorToken, mark.color);
    if (value === mark.color) return mark;
    changed = true;
    return { ...mark, color: value };
  });
  return changed ? { ...element, marks } : element;
}

function applyElement(source: SceneElement, tokens: BrandTokens, scale: number): SceneElement {
  const element = applyChartPalette(applyMarks(source, tokens), tokens);
  const binding = element.brand;
  if (!binding) return element;
  let out = element;
  const edit = () => {
    if (out === element) out = { ...element } as SceneElement;
    return out as Record<string, unknown>;
  };

  if (binding.fill && element.type === "shape" && typeof element.fill === "string") {
    const value = colorValue(tokens, binding.fill, element.fill);
    if (value !== element.fill) edit().fill = value;
  }
  if (
    binding.color &&
    (element.type === "icon" || element.type === "path" || element.type === "chart")
  ) {
    const value = colorValue(tokens, binding.color, element.color);
    if (value !== element.color) edit().color = value;
  }
  if (element.type === "qr") {
    if (binding.color) {
      const value = colorValue(tokens, binding.color, element.color);
      if (value !== element.color) edit().color = value;
    }
    if (binding.fill) {
      const value = colorValue(tokens, binding.fill, element.background);
      if (value !== element.background) edit().background = value;
    }
  }
  if (element.type === "table") {
    if (binding.fill) {
      const value = colorValue(tokens, binding.fill, element.headerFill);
      if (value !== element.headerFill) edit().headerFill = value;
    }
    if (binding.color) {
      const value = colorValue(tokens, binding.color, element.color);
      if (value !== element.color) edit().color = value;
    }
    if (binding.stroke) {
      const value = colorValue(tokens, binding.stroke, element.borderColor);
      if (value !== element.borderColor) edit().borderColor = value;
    }
  }
  if (binding.stroke && (element.type === "shape" || element.type === "image") && element.stroke) {
    const value = colorValue(tokens, binding.stroke, element.stroke.color);
    if (value !== element.stroke.color) edit().stroke = { ...element.stroke, color: value };
  }
  if (binding.radius && (element.type === "shape" || element.type === "image")) {
    const value = round(
      brandRadius(tokens, binding.radius) * (binding.radius === "full" ? 1 : scale),
    );
    if (value !== element.radius) edit().radius = value;
  }
  if (element.type === "text") {
    let style = element.style;
    const editStyle = () => {
      if (style === element.style) style = { ...element.style };
      return style as Record<string, unknown>;
    };
    if (binding.color) {
      const value = colorValue(tokens, binding.color, style.color);
      if (value !== style.color) editStyle().color = value;
    }
    if (binding.textStroke && style.stroke) {
      const value = colorValue(tokens, binding.textStroke, style.stroke.color);
      if (value !== style.stroke.color) editStyle().stroke = { ...style.stroke, color: value };
    }
    if (binding.text) {
      const textStyle = brandTextStyle(tokens, binding.text);
      if (textStyle) {
        const values = brandTypography(textStyle, scale * (element.textScale ?? 1));
        for (const field of TEXT_STYLE_FIELDS) {
          if (style[field] !== values[field]) editStyle()[field] = values[field];
        }
      }
    }
    if (style !== element.style) edit().style = style;
  }
  if (element.type === "captions") {
    let style = element.style;
    const editStyle = () => {
      if (style === element.style) style = { ...element.style };
      return style as Record<string, unknown>;
    };
    if (binding.color) {
      const value = colorValue(tokens, binding.color, style.color);
      if (value !== style.color) editStyle().color = value;
    }
    if (binding.fill) {
      const value = colorValue(tokens, binding.fill, style.highlightColor);
      if (value !== style.highlightColor) editStyle().highlightColor = value;
    }
    if (binding.textStroke && style.stroke) {
      const value = colorValue(tokens, binding.textStroke, style.stroke.color);
      if (value !== style.stroke.color) editStyle().stroke = { ...style.stroke, color: value };
    }
    if (binding.text) {
      const textStyle = brandTextStyle(tokens, binding.text);
      if (textStyle) {
        const values = captionTypography(textStyle, style.fontWeight);
        for (const field of CAPTION_TEXT_FIELDS) {
          if (style[field] !== values[field]) editStyle()[field] = values[field];
        }
      }
    }
    if (style !== element.style) edit().style = style;
  }
  return out;
}

function applyPage(page: ScenePage, tokens: BrandTokens): ScenePage {
  const key = page.brand?.background;
  if (!key || typeof page.background !== "string") return page;
  const value = colorValue(tokens, key, page.background);
  return value === page.background ? page : { ...page, background: value };
}

/** Refreshes all bound values from the kit. Returns the same object when nothing changed. */
export function applyBrand(doc: SceneDocument, tokens: BrandTokens): SceneDocument {
  const scale = brandScale(doc);
  let elements = doc.elements;
  for (const [id, element] of Object.entries(doc.elements)) {
    const next = applyElement(element, tokens, scale);
    if (next !== element) {
      if (elements === doc.elements) elements = { ...doc.elements };
      elements[id] = next;
    }
  }
  let pages = doc.pages;
  doc.pages.forEach((page, index) => {
    const next = applyPage(page, tokens);
    if (next !== page) {
      if (pages === doc.pages) pages = [...doc.pages];
      pages[index] = next;
    }
  });
  return elements === doc.elements && pages === doc.pages ? doc : { ...doc, elements, pages };
}

/** Print swatches of the document plus the kit's exact CMYK colours (the document wins). */
export function mergeSwatches(
  documentSwatches: Swatch[] | undefined,
  tokens: BrandTokens,
): Swatch[] {
  const own = documentSwatches ?? [];
  const taken = new Set(own.map((swatch) => opaqueHex(swatch.color)));
  return [...brandSwatches(tokens).filter((swatch) => !taken.has(opaqueHex(swatch.color))), ...own];
}

/** Document as the renderer/exporter should see it: bindings resolved, brand CMYK values added. */
export function withBrand(doc: SceneDocument, tokens: BrandTokens): SceneDocument {
  const resolved = applyBrand(doc, tokens);
  if (!resolved.format.print) return resolved;
  const swatches = mergeSwatches(resolved.format.print.swatches, tokens);
  if (swatches.length === 0) return resolved;
  return {
    ...resolved,
    format: { ...resolved.format, print: { ...resolved.format.print, swatches } },
  };
}

// ---------------------------------------------------------------------------
// Keeping bindings honest while editing
// ---------------------------------------------------------------------------

const colorKey = (value: string | undefined) => (value ? opaqueHex(value) : undefined);

/** The value a binding slot controls, in a comparable form. */
function slotValue(element: SceneElement, slot: BrandBindingSlot): string | number | undefined {
  switch (slot) {
    case "fill":
      if (element.type === "captions") return colorKey(element.style.highlightColor);
      if (element.type === "qr") return colorKey(element.background);
      if (element.type === "table") return colorKey(element.headerFill);
      return element.type === "shape"
        ? typeof element.fill === "string"
          ? colorKey(element.fill)
          : JSON.stringify(element.fill)
        : undefined;
    case "stroke":
      if (element.type === "table") return colorKey(element.borderColor);
      return element.type === "shape" || element.type === "image"
        ? colorKey(element.stroke?.color)
        : undefined;
    case "color":
      if (
        element.type === "qr" ||
        element.type === "icon" ||
        element.type === "path" ||
        element.type === "chart" ||
        element.type === "table"
      )
        return colorKey(element.color);
      return element.type === "text" || element.type === "captions"
        ? colorKey(element.style.color)
        : undefined;
    case "textStroke":
      return element.type === "text" || element.type === "captions"
        ? colorKey(element.style.stroke?.color)
        : undefined;
    case "text":
      if (element.type === "captions") {
        return CAPTION_TEXT_FIELDS.map((field) => String(element.style[field])).join("|");
      }
      return element.type === "text"
        ? TEXT_STYLE_FIELDS.map((field) => String(element.style[field])).join("|")
        : undefined;
    case "radius":
      return element.type === "shape" || element.type === "image" ? element.radius : undefined;
  }
}

function withoutSlots<T extends { brand?: BrandBinding | undefined }>(
  item: T,
  slots: BrandBindingSlot[],
): T {
  const brand = { ...item.brand };
  for (const slot of slots) delete brand[slot];
  return { ...item, brand: Object.keys(brand).length > 0 ? brand : undefined };
}

/**
 * Removes bindings whose value was changed by hand between `before` and
 * `after` ("detach on edit"). A binding set in the same change is kept, and
 * colour bindings survive pure transparency changes.
 */
export function pruneBindings(before: SceneDocument, after: SceneDocument): SceneDocument {
  if (before === after) return after;
  let elements = after.elements;
  for (const [id, element] of Object.entries(after.elements)) {
    const previous = before.elements[id];
    if (!previous || previous === element || !element.brand) continue;
    const stale: BrandBindingSlot[] = [];
    for (const slot of Object.keys(element.brand) as BrandBindingSlot[]) {
      if (!element.brand[slot] || previous.brand?.[slot] !== element.brand[slot]) continue;
      if (slotValue(previous, slot) !== slotValue(element, slot)) stale.push(slot);
    }
    if (stale.length > 0) {
      if (elements === after.elements) elements = { ...after.elements };
      const next = withoutSlots(element, stale);
      // The size factor only means something while the text style is linked.
      if (next.type === "text" && stale.includes("text")) delete next.textScale;
      elements[id] = next;
    }
  }
  let pages = after.pages;
  after.pages.forEach((page, index) => {
    const previous = before.pages.find((candidate) => candidate.id === page.id);
    if (!previous || previous === page || !page.brand?.background) return;
    if (previous.brand?.background !== page.brand.background) return;
    const changed =
      typeof page.background !== "string" ||
      typeof previous.background !== "string" ||
      opaqueHex(page.background) !== opaqueHex(previous.background);
    if (changed) {
      if (pages === after.pages) pages = [...after.pages];
      pages[index] = { ...page, brand: undefined };
    }
  });
  return elements === after.elements && pages === after.pages
    ? after
    : { ...after, elements, pages };
}

// ---------------------------------------------------------------------------
// Binding helpers (mutating; for Immer drafts and plain objects)
// ---------------------------------------------------------------------------

type ColorSlot = "fill" | "stroke" | "color" | "textStroke";

/** Sets a colour slot to a brand colour and links it. Returns false if the slot does not apply. */
export function bindColor(
  element: SceneElement,
  slot: ColorSlot,
  key: string,
  tokens: BrandTokens,
): boolean {
  const color = brandColor(tokens, key)?.color;
  if (!color) return false;
  if (slot === "fill" && element.type === "shape") {
    element.fill = typeof element.fill === "string" ? keepAlpha(color, element.fill) : color;
  } else if (slot === "stroke" && (element.type === "shape" || element.type === "image")) {
    if (!element.stroke) return false;
    element.stroke.color = keepAlpha(color, element.stroke.color);
  } else if (slot === "color" && element.type === "text") {
    element.style.color = keepAlpha(color, element.style.color);
  } else if (
    slot === "color" &&
    (element.type === "qr" ||
      element.type === "icon" ||
      element.type === "path" ||
      element.type === "chart")
  ) {
    element.color = keepAlpha(color, element.color);
  } else if (element.type === "table" && slot !== "textStroke") {
    if (slot === "fill") element.headerFill = keepAlpha(color, element.headerFill);
    else if (slot === "stroke") element.borderColor = keepAlpha(color, element.borderColor);
    else element.color = keepAlpha(color, element.color);
  } else if (slot === "fill" && element.type === "qr") {
    element.background = keepAlpha(color, element.background);
  } else if (slot === "textStroke" && element.type === "text") {
    if (!element.style.stroke) return false;
    element.style.stroke.color = keepAlpha(color, element.style.stroke.color);
  } else if (element.type === "captions" && slot !== "stroke") {
    if (slot === "color") element.style.color = keepAlpha(color, element.style.color);
    else if (slot === "fill")
      element.style.highlightColor = keepAlpha(color, element.style.highlightColor);
    else {
      if (!element.style.stroke) return false;
      element.style.stroke.color = keepAlpha(color, element.style.stroke.color);
    }
  } else return false;
  element.brand = { ...element.brand, [slot]: key };
  return true;
}

/**
 * Applies a text style (and its default colour) to a text element and links
 * both. `scale` comes from `brandScale(doc)`.
 */
export function bindTextStyle(
  element: SceneElement,
  key: string,
  tokens: BrandTokens,
  scale: number,
  options: { withColor?: boolean } = {},
): boolean {
  if (element.type === "captions") {
    const style = brandTextStyle(tokens, key);
    if (!style) return false;
    Object.assign(element.style, captionTypography(style, element.style.fontWeight));
    element.brand = { ...element.brand, text: key };
    return true;
  }
  if (element.type !== "text") return false;
  const style = brandTextStyle(tokens, key);
  if (!style) return false;
  Object.assign(element.style, brandTypography(style, scale));
  element.brand = { ...element.brand, text: key };
  // Choosing a style means its size, also where a template scaled it.
  delete element.textScale;
  if (options.withColor !== false && style.color) bindColor(element, "color", style.color, tokens);
  return true;
}

export function bindRadius(
  element: SceneElement,
  key: NonNullable<BrandBinding["radius"]>,
  tokens: BrandTokens,
  scale: number,
): boolean {
  if (element.type !== "shape" && element.type !== "image") return false;
  element.radius = round(brandRadius(tokens, key) * (key === "full" ? 1 : scale));
  element.brand = { ...element.brand, radius: key };
  return true;
}

/** "Vom Brand lösen": keeps the current values, removes the links. */
export function unbindElement(element: SceneElement, slots?: BrandBindingSlot[]): void {
  if (!element.brand) return;
  if (element.type === "text" && (!slots || slots.includes("text"))) delete element.textScale;
  if (!slots) {
    delete element.brand;
    return;
  }
  for (const slot of slots) delete element.brand[slot];
  if (Object.keys(element.brand).length === 0) delete element.brand;
}

/** Bindings of an element whose token no longer exists in the kit. */
export function missingTokens(element: SceneElement, tokens: BrandTokens): BrandBindingSlot[] {
  const binding = element.brand;
  if (!binding) return [];
  return (Object.keys(binding) as BrandBindingSlot[]).filter((slot) => {
    const key = binding[slot];
    if (!key) return false;
    if (slot === "text") return !brandTextStyle(tokens, key);
    if (slot === "radius") return false;
    return !brandColor(tokens, key);
  });
}

/** Number of elements per token key in a document (for "wird verwendet" warnings). */
export function tokenUsage(doc: SceneDocument): Map<string, number> {
  const usage = new Map<string, number>();
  const add = (key: string | undefined) => {
    if (key) usage.set(key, (usage.get(key) ?? 0) + 1);
  };
  for (const element of Object.values(doc.elements)) {
    for (const [slot, key] of Object.entries(element.brand ?? {})) {
      add(slot === "text" ? `text:${key}` : slot === "radius" ? `radius:${key}` : `color:${key}`);
    }
  }
  for (const page of doc.pages) add(page.brand?.background && `color:${page.brand.background}`);
  return usage;
}

// ---------------------------------------------------------------------------
// New documents in the brand
// ---------------------------------------------------------------------------

/**
 * Starting point for a new document in a kit: page backgrounds linked to the
 * brand background, print settings from the kit's default print profile.
 */
export function brandNewDocument(doc: SceneDocument, tokens: BrandTokens): SceneDocument {
  const background = brandColor(tokens, "background");
  const pages = doc.pages.map((page) =>
    background && typeof page.background === "string"
      ? { ...page, background: background.color, brand: { background: "background" } }
      : page,
  );
  let print = doc.format.print;
  if (print && (tokens.print.provider || tokens.print.paper || tokens.print.profile)) {
    const provider = tokens.print.provider ? getPrintProvider(tokens.print.provider) : null;
    print = {
      ...print,
      ...(provider
        ? { provider: provider.id, bleedMm: provider.bleedMm, safeMm: provider.safeMm }
        : {}),
      ...(tokens.print.paper ? { paper: tokens.print.paper } : {}),
      ...(tokens.print.profile ? { profile: tokens.print.profile } : {}),
    };
  }
  return { ...doc, pages, format: { ...doc.format, print } };
}
